import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { 
  ChevronLeft, Calendar, Clock, MapPin, Users, 
  CreditCard, Check, Shield, Lock, Sparkles
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

export const EventPaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const event = location.state?.event;

  const [formData, setFormData] = useState({
    name: user?.username || "",
    email: user?.email || "",
    phone: "",
    gender: user?.gender || "",
    birthYear: user?.birth_year || "",
    paymentMethod: "card"
  });

  const [isProcessing, setIsProcessing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  if (!event) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] pt-24 px-6">
        <div className="max-w-2xl mx-auto text-center py-20">
          <h1 className="text-2xl font-bold text-stone-800 mb-4">이벤트 정보를 찾을 수 없습니다</h1>
          <Link to="/offline-events" className="text-burgundy-500 hover:underline">
            이벤트 목록으로 돌아가기
          </Link>
        </div>
      </div>
    );
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    
    // 결제 처리 시뮬레이션 (실제로는 Stripe 등 결제 API 연동)
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsProcessing(false);
    setIsComplete(true);
  };

  if (isComplete) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] pt-24 px-6">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-stone-800 mb-4">
              신청이 완료되었습니다!
            </h1>
            <p className="text-stone-600 mb-8 leading-relaxed">
              <span className="font-semibold text-burgundy-600">{event.title}</span> 참가 신청이 정상적으로 완료되었습니다.<br />
              입력하신 이메일로 확인 메일이 발송됩니다.
            </p>
            
            <div className="bg-stone-50 rounded-xl p-6 mb-8 text-left">
              <h3 className="font-semibold text-stone-800 mb-4">신청 정보</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-stone-500">이벤트</span>
                  <span className="font-medium">{event.title}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-stone-500">일시</span>
                  <span className="font-medium">{event.date} {event.time}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-stone-500">장소</span>
                  <span className="font-medium">{event.location}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-stone-500">결제 금액</span>
                  <span className="font-semibold text-burgundy-600">{event.price?.toLocaleString()}원</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                variant="outline"
                onClick={() => navigate("/offline-events")}
                className="px-6"
              >
                다른 이벤트 보기
              </Button>
              <Button 
                className="btn-primary px-6"
                onClick={() => navigate("/")}
              >
                홈으로 돌아가기
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9] pt-20">
      {/* Header */}
      <div className="bg-stone-900 py-8">
        <div className="max-w-6xl mx-auto px-6">
          <Link 
            to="/offline-events"
            className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-4 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            이벤트 목록으로
          </Link>
          <h1 className="text-2xl md:text-3xl font-bold text-white">참가 신청하기</h1>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Form Section */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Info */}
              <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
                <h2 className="text-xl font-bold text-stone-800 mb-6 flex items-center gap-2">
                  <span className="w-8 h-8 bg-burgundy-100 text-burgundy-600 rounded-full flex items-center justify-center text-sm font-bold">1</span>
                  신청자 정보
                </h2>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">이름 *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-stone-200 focus:border-burgundy-500 focus:ring-2 focus:ring-burgundy-200 outline-none transition-all"
                      placeholder="홍길동"
                      data-testid="input-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">이메일 *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-stone-200 focus:border-burgundy-500 focus:ring-2 focus:ring-burgundy-200 outline-none transition-all"
                      placeholder="example@email.com"
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">연락처 *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-stone-200 focus:border-burgundy-500 focus:ring-2 focus:ring-burgundy-200 outline-none transition-all"
                      placeholder="010-1234-5678"
                      data-testid="input-phone"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">성별 *</label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-stone-200 focus:border-burgundy-500 focus:ring-2 focus:ring-burgundy-200 outline-none transition-all"
                      data-testid="select-gender"
                    >
                      <option value="">선택해주세요</option>
                      <option value="male">남성</option>
                      <option value="female">여성</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">출생년도 *</label>
                    <input
                      type="number"
                      name="birthYear"
                      value={formData.birthYear}
                      onChange={handleInputChange}
                      required
                      min="1950"
                      max="2000"
                      className="w-full px-4 py-3 rounded-lg border border-stone-200 focus:border-burgundy-500 focus:ring-2 focus:ring-burgundy-200 outline-none transition-all"
                      placeholder="1980"
                      data-testid="input-birthyear"
                    />
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
                <h2 className="text-xl font-bold text-stone-800 mb-6 flex items-center gap-2">
                  <span className="w-8 h-8 bg-burgundy-100 text-burgundy-600 rounded-full flex items-center justify-center text-sm font-bold">2</span>
                  결제 방법
                </h2>
                
                <div className="space-y-3">
                  <label className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${formData.paymentMethod === 'card' ? 'border-burgundy-500 bg-burgundy-50' : 'border-stone-200 hover:border-stone-300'}`}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="card"
                      checked={formData.paymentMethod === 'card'}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${formData.paymentMethod === 'card' ? 'border-burgundy-500' : 'border-stone-300'}`}>
                      {formData.paymentMethod === 'card' && <div className="w-3 h-3 rounded-full bg-burgundy-500" />}
                    </div>
                    <CreditCard className="w-6 h-6 text-stone-600" />
                    <div>
                      <p className="font-medium text-stone-800">신용/체크카드</p>
                      <p className="text-sm text-stone-500">국내외 모든 카드 결제 가능</p>
                    </div>
                  </label>

                  <label className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${formData.paymentMethod === 'transfer' ? 'border-burgundy-500 bg-burgundy-50' : 'border-stone-200 hover:border-stone-300'}`}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="transfer"
                      checked={formData.paymentMethod === 'transfer'}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${formData.paymentMethod === 'transfer' ? 'border-burgundy-500' : 'border-stone-300'}`}>
                      {formData.paymentMethod === 'transfer' && <div className="w-3 h-3 rounded-full bg-burgundy-500" />}
                    </div>
                    <svg className="w-6 h-6 text-stone-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    <div>
                      <p className="font-medium text-stone-800">계좌이체</p>
                      <p className="text-sm text-stone-500">실시간 계좌이체</p>
                    </div>
                  </label>

                  <label className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${formData.paymentMethod === 'kakao' ? 'border-burgundy-500 bg-burgundy-50' : 'border-stone-200 hover:border-stone-300'}`}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="kakao"
                      checked={formData.paymentMethod === 'kakao'}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${formData.paymentMethod === 'kakao' ? 'border-burgundy-500' : 'border-stone-300'}`}>
                      {formData.paymentMethod === 'kakao' && <div className="w-3 h-3 rounded-full bg-burgundy-500" />}
                    </div>
                    <div className="w-6 h-6 bg-yellow-400 rounded flex items-center justify-center">
                      <span className="text-xs font-bold text-stone-800">K</span>
                    </div>
                    <div>
                      <p className="font-medium text-stone-800">카카오페이</p>
                      <p className="text-sm text-stone-500">카카오페이로 간편 결제</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Submit Button */}
              <Button 
                type="submit"
                disabled={isProcessing}
                className="w-full bg-gradient-to-r from-burgundy-500 to-burgundy-600 hover:from-burgundy-600 hover:to-burgundy-700 text-white py-4 text-lg font-semibold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-payment-btn"
              >
                {isProcessing ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    결제 처리 중...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Lock className="w-5 h-5" />
                    {event.price?.toLocaleString()}원 결제하기
                  </span>
                )}
              </Button>

              {/* Security Notice */}
              <div className="flex items-center justify-center gap-2 text-sm text-stone-500">
                <Shield className="w-4 h-4" />
                <span>SSL 암호화로 안전하게 보호됩니다</span>
              </div>
            </form>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-24">
              <h2 className="text-lg font-bold text-stone-800 mb-4">주문 요약</h2>
              
              {/* Event Card */}
              {event.poster && (
                <img 
                  src={event.poster} 
                  alt={event.title}
                  className="w-full h-40 object-cover rounded-xl mb-4"
                />
              )}
              
              <h3 className="font-bold text-stone-800 text-lg mb-3">{event.title}</h3>
              
              <div className="space-y-3 text-sm mb-6">
                <div className="flex items-center gap-2 text-stone-600">
                  <Calendar className="w-4 h-4 text-burgundy-500" />
                  <span>{event.date}</span>
                </div>
                <div className="flex items-center gap-2 text-stone-600">
                  <Clock className="w-4 h-4 text-burgundy-500" />
                  <span>{event.time}</span>
                </div>
                <div className="flex items-center gap-2 text-stone-600">
                  <MapPin className="w-4 h-4 text-burgundy-500" />
                  <span>{event.location}</span>
                </div>
                <div className="flex items-center gap-2 text-stone-600">
                  <Users className="w-4 h-4 text-burgundy-500" />
                  <span>정원 {event.maxAttendees}명</span>
                </div>
              </div>

              <div className="border-t border-stone-200 pt-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-stone-600">참가비</span>
                  <span className="font-medium">{event.price?.toLocaleString()}원</span>
                </div>
                <div className="flex justify-between items-center text-lg font-bold">
                  <span className="text-stone-800">총 결제금액</span>
                  <span className="text-burgundy-600">{event.price?.toLocaleString()}원</span>
                </div>
              </div>

              {/* Benefits */}
              <div className="mt-6 bg-stone-50 rounded-xl p-4">
                <p className="text-xs font-semibold text-stone-700 mb-2 flex items-center gap-1">
                  <Sparkles className="w-4 h-4 text-burgundy-500" />
                  포함 사항
                </p>
                <ul className="text-xs text-stone-600 space-y-1">
                  <li className="flex items-center gap-2">
                    <Check className="w-3 h-3 text-green-500" />
                    프리미엄 음식 & 음료
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-3 h-3 text-green-500" />
                    전문 호스트 진행
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-3 h-3 text-green-500" />
                    네트워킹 프로그램
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventPaymentPage;
